import { ContentItem, Controllers, Sort, CategoryMini, ContentItemFieldValue, CreatedWithMessages, PagedResult } from "@infoland/iprova-api";
import { ContentItemsRequest } from "../../../types/ContentItemsRequest";
import { ContentItemDetails } from "@/types/contentItemDetails";
import i18n from "../../../plugins/i18n";
import { ApiProxy, ApiCallResult, PagedResult as ApiProxyPagedResult } from "@infoland/api-proxy";
import { PostFilter } from "../../../types/filter/postFilter";
import snackBarService from "../../../services/snackBarService";
import storeHelper from "../../../store/store-helper";

const daysForRecentItemsInThePast = 31;
// state type
export interface ContentItemState {
    recentContentItems: ContentItem[];
    contentItems: ContentItem[];
    numberOfAvailableContentItems: number;
    fillOutContentItems: ContentItem[];
    totalNumberOfFillOutContentItems: number;
    contentItemDetails: ContentItemFieldValue[];
    searchSuggestionCorrection: string;
    includeSearchSuggestionCorrection: boolean;
    numberOfAvailableContentItemsForHyperlink: number;
}

export function initialize(): ContentItemState {
    return {
        recentContentItems: null,
        contentItems: null,
        fillOutContentItems: null,
        totalNumberOfFillOutContentItems: 0,
        numberOfAvailableContentItems: 0,
        contentItemDetails: null,
        searchSuggestionCorrection: null,
        includeSearchSuggestionCorrection: true,
        numberOfAvailableContentItemsForHyperlink: 0
    };
}

// initial state
const state: ContentItemState = initialize();

// getters for this store
const getters = {
    /**
     * The recent content items
     * @param state The state to read from
     */
    recentContentItems(state: ContentItemState): ContentItem[] {
        return state.recentContentItems;
    },
    /**
     * The browse/search content items
     * @param state The state to read from
     */
    contentItems(state: ContentItemState): ContentItem[] {
        return state.contentItems;
    },
    numberOfAvailableContentItems(state: ContentItemState): number {
        return state.numberOfAvailableContentItems;
    },

    /**
     * The total number of fill out content items
     * @param state The state to read from
     */
    totalNumberOfFillOutContentItems(state: ContentItemState): number {
        return state.totalNumberOfFillOutContentItems;
    },

    /**
     * The fill out content items
     * @param state The state to read from
     */
    fillOutContentItems(state: ContentItemState): ContentItem[] {
        return state.fillOutContentItems;
    },
    contentItemDetails(state: ContentItemState): ContentItemFieldValue[] {
        return state.contentItemDetails;
    },
    /**
     * The searched suggestion correction
     * @param state The state to read from
     */
    searchSuggestionCorrection(state: ContentItemState): string {
        return state.searchSuggestionCorrection;
    },

    /**
     * Specify whether or not a search correction is required from server
     * @param state The state to read from
     */
    includeSearchSuggestionCorrection(state: ContentItemState): boolean {
        return state.includeSearchSuggestionCorrection;
    },

    /**
     * The total number of content items available for creating hyperlink
     * @param state The state to read from
     */
    numberOfAvailableContentItemsForHyperlink(state: ContentItemState): number {
        return state.numberOfAvailableContentItemsForHyperlink;
    }
};

// mutations for this store
const mutations = {
    /**
     * Set the recent content items
     * @param state The state to write to
     * @param contentItems the returned content items from recent ones
     */
    _recentContentItems(state: ContentItemState, contentItems: ContentItem[]): void {
        // undefined should become null
        if (!contentItems) {
            contentItems = null;
        }

        state.recentContentItems = contentItems;
    },

    /**
     * Sets the browse/search content items
     * @param state The state to write to
     * @param contentItems The returned content items from search
     */
    _contentItems(state: ContentItemState, contentItems: ContentItem[]): void {
        // undefined should become null
        if (!contentItems) {
            contentItems = null;
        }

        state.contentItems = contentItems;
    },
    /**
     * Set the numberOfContentItems
     * @param state The state to write to
     * @param numberOfItems the number of all content Items that are available based on search criteria
     */
    _numberOfAvailableContentItems(state: ContentItemState, numberOfItems: number): void {
        if (!numberOfItems) {
            numberOfItems = 0;
        }

        state.numberOfAvailableContentItems = numberOfItems;
    },

    /**
     * Sets the fill out content items
     * @param state The state to write to
     * @param fillOutContentItems The returned fill out content items
     */
    _fillOutContentItems(state: ContentItemState, fillOutContentItems: ContentItem[]): void {
        if (!fillOutContentItems) {
            fillOutContentItems = null;
        }

        state.fillOutContentItems = fillOutContentItems;
    },

    /**
     *
     * @param state The state to write to
     * @param totalNumberOfFillOutContentItems The total number of fill out content items
     */
    _totalNumberOfFillOutContentItems(state: ContentItemState, totalNumberOfFillOutContentItems: number): void {
        if (!totalNumberOfFillOutContentItems) {
            totalNumberOfFillOutContentItems = 0;
        }

        state.totalNumberOfFillOutContentItems = totalNumberOfFillOutContentItems;
    },

    /**
     * Set the portal id
     * @param state The state to write to
     * @param contentItems the returned content items from search
     */
    _contentItemDetails(state: ContentItemState, contentItemDetails: ContentItemFieldValue[]): void {
        // undefined should become null
        if (!contentItemDetails) {
            contentItemDetails = null;
        }

        state.contentItemDetails = contentItemDetails;
    },

    /**
     * Set the search suggestion
     * @param state The state to write to
     * @param searchSuggestionCorrection the returned suggestion from search
     */
    _searchSuggestionCorrection(state: ContentItemState, searchSuggestionCorrection: string): void {
        state.searchSuggestionCorrection = searchSuggestionCorrection;
    },

    /**
     * Set the includeSearchSuggestionCorrection
     * @param state The state to write to
     * @param includeSearchSuggestionCorrection the value that will be sent to the server to specify if a search correction is required
     */
    _includeSearchSuggestionCorrection(state: ContentItemState, includeSearchSuggestionCorrection: boolean): void {
        state.includeSearchSuggestionCorrection = includeSearchSuggestionCorrection;
    },

    /**
     * Set the numberOfAvailableContentItemsForHyperlink
     * @param state The state to write to
     * @param numberOfAvailableContentItemsForHyperlink the number of all content Items that are available for creating a hyperlink
     */
    _numberOfAvailableContentItemsForHyperlink(state: ContentItemState, numberOfAvailableContentItemsForHyperlink: number): void {
        if (!numberOfAvailableContentItemsForHyperlink) {
            numberOfAvailableContentItemsForHyperlink = 0;
        }
        state.numberOfAvailableContentItemsForHyperlink = numberOfAvailableContentItemsForHyperlink;
    },

    /**
     * Reset state to initial values
     * @param state current state
     */
    reset(state: ContentItemState) {
        Object.assign(state, initialize());
    }
};

// actions for this store
const actions = {
    async loadRecentContentItemsAsync({ commit }, params): Promise<ContentItem[]> {
        const date: Date = new Date();
        date.setDate(date.getDate() - daysForRecentItemsInThePast);
        if (params.text_contains) params.text_contains = encodeURIComponent(params.text_contains);
        const getResponse: PagedResult<ContentItem> = await Controllers.ContentItemController.getContentItemsAsync(
            params.text_contains,
            params.portalId,
            false,
            true,
            Sort.last_visited_date_time,
            date
        );
        commit("_recentContentItems", getResponse.items);
        return getResponse.items;
    },

    async getContentItemsForHyperlinkAsync({ commit }, searchParams: ContentItemsRequest): Promise<ContentItem[]> {
        const path =
            `portals/content_items?global_behaviour=all&include_sub_type_field=true&include_content_type=true&include_icons=true` +
            `&text_contains=${encodeURIComponent(searchParams.text_contains)}` +
            `&sort=${Sort.none}` +
            `&offset=${searchParams.offset}` +
            `&limit=${searchParams.limit}`;

        const getResponse: ApiCallResult<ContentItem[]> = await ApiProxy.getRequest(snackBarService.setMessage, path);
        const response = new ApiProxyPagedResult<ContentItem>(getResponse);

        let contentItems: Array<ContentItem> = response.items;
        commit("_numberOfAvailableContentItemsForHyperlink", response.totalItems);
        return contentItems;
    },

    async loadContentItemsAsync({ commit }, searchParams: ContentItemsRequest): Promise<{ items: ContentItem[]; filterId: string }> {
        let lastFilter: PostFilter = storeHelper.contentItemFiltersStore.filter || {};
        if (!AreEqual(searchParams, lastFilter)) {
            //create new filter
            lastFilter.text_contains = searchParams.text_contains;
            lastFilter.portal_id = searchParams.portal_id;
            lastFilter.collection_ids = searchParams.collection_ids;
            lastFilter.is_recommended_link = searchParams.is_recommended_link;
            lastFilter.use_settings_from_portal_id = searchParams.use_settings_from_portal_id;

            const filter: PostFilter = {
                portal_id: searchParams.portal_id,
                text_contains: searchParams.text_contains,
                collection_ids: searchParams.collection_ids,
                is_recommended_link: searchParams.is_recommended_link,
                use_settings_from_portal_id: searchParams.use_settings_from_portal_id
            };

            const path = "/portals/content_items/filter";
            const response: CreatedWithMessages = await Controllers.GenericController.postAsync<PostFilter, CreatedWithMessages>(path, filter);
            if (response) {
                storeHelper.contentItemFiltersStore.updateLastUsedFilter(response.created_identifier, lastFilter);
            }
        }

        const filterId: string = storeHelper.contentItemFiltersStore.filterId;
        if (filterId) {
            const getSearchSuggestion: boolean = searchParams.text_contains && state.includeSearchSuggestionCorrection;
            const pathGetContentItemByFilter =
                `portals/content_items/filter/${filterId}` +
                `?offset=${searchParams.offset}` +
                `&limit=${searchParams.limit}` +
                `&include_search_suggestion_correction=${getSearchSuggestion ? "true" : "false"}` +
                `&include_sub_type_field=true` +
                `&include_content_type=true`;

            const pagedResult = await Controllers.GenericController.getPagedResultAsync<ContentItem>(pathGetContentItemByFilter);
            commit("_contentItems", pagedResult.items);
            commit("_numberOfAvailableContentItems", pagedResult.totalItems);

            //convert pagedResult.searchSuggestionCorrection from base64 to string
            if (pagedResult.searchSuggestionCorrection)
                pagedResult.searchSuggestionCorrection = decodeURIComponent(escape(atob(pagedResult.searchSuggestionCorrection)));

            commit("_searchSuggestionCorrection", pagedResult.searchSuggestionCorrection);

            return { items: pagedResult.items, filterId: filterId };
        } else {
            commit("_contentItems", null);
            commit("_numberOfAvailableContentItems", null);
            commit("_searchSuggestionCorrection", null);
            return null;
        }
    },
    async loadFillOutContentItemsAsync(
        { commit },
        searchParams: { text_contains: string; portal_id: number; limit: number; offset: number }
    ): Promise<ContentItem[]> {
        const getSearchSuggestion: boolean = searchParams.text_contains && state.includeSearchSuggestionCorrection;
        if (getSearchSuggestion) searchParams.text_contains = encodeURIComponent(searchParams.text_contains);
        const getResponse: PagedResult<ContentItem> = await Controllers.ContentItemController.getContentItemsAsync(
            searchParams.text_contains,
            searchParams.portal_id,
            true,
            true,
            Sort.category + "," + Sort.title,
            null,
            searchParams.limit,
            searchParams.offset,
            true,
            null,
            false,
            getSearchSuggestion
        );
        let fillOutItems: ContentItem[] = getResponse.items;
        if (fillOutItems) {
            fillOutItems = fillOutItems.map(item => {
                if (item.category == null) {
                    if (item.content_type.content_type_id === 1) {
                        const fillableForms: CategoryMini = {
                            category_id: -2,
                            name: i18n.t("portal.portals.fillOut.fillableForms").toString()
                        };

                        item.category = fillableForms;
                    } else {
                        const noCategory: CategoryMini = {
                            category_id: -1,
                            name: i18n.t("portal.portals.fillOut.noCategoryName").toString()
                        };

                        item.category = noCategory;
                    }
                }

                return item;
            });
        }
        commit("_fillOutContentItems", fillOutItems);
        commit("_totalNumberOfFillOutContentItems", getResponse.totalItems);
        commit("_searchSuggestionCorrection", getResponse.searchSuggestionCorrection);
        return fillOutItems;
    },

    async loadContentItemDetailsAsync({ commit }, contentItemDetails: ContentItemDetails): Promise<ContentItemFieldValue[]> {
        const requestUrl = `portals/${contentItemDetails.portalId ? contentItemDetails.portalId + "/" : ""}content_items/${
            contentItemDetails.contentItemId
        }/details`;
        const response = await Controllers.GenericController.getAsync<ContentItemFieldValue[]>(requestUrl);
        commit("_contentItemDetails", response);
        return response;
    },

    setIncludeSearchSuggestionCorrection({ commit }, includeSearchSuggestionCorrection: boolean): void {
        commit("_includeSearchSuggestionCorrection", includeSearchSuggestionCorrection);
    }
};

/**
 * Check for equal values between searchRequest and filter
 * @param searchRequest
 * @param filter
 * @returns true when the values are the same
 */
function AreEqual(searchRequest: ContentItemsRequest, filter: PostFilter): boolean {
    if (searchRequest.text_contains !== filter.text_contains) return false;
    if (searchRequest.use_settings_from_portal_id !== filter.use_settings_from_portal_id) return false;
    if (searchRequest.portal_id !== filter.portal_id) return false;
    if (searchRequest.is_recommended_link !== filter.is_recommended_link) return false;
    if (JSON.stringify(searchRequest.collection_ids?.sort()) !== JSON.stringify(filter.collection_ids?.sort())) return false;

    return true;
}

// export the namespace
export const namespace = "contentItems";

// export the store 'module'
export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
};
